import React, { useCallback, useMemo } from 'react';
import { StyledContainer } from '../../../components/Table/TableContainer';
import { TableHeader } from '../../../components/Table/TableHeader';
import { StyledRowCell, TableRow } from '../../../components/Table/TableRow';
import { ReactComponent as CirclePlusIcon } from './Icons/CirclePlus.svg';
import { ReactComponent as PlantingIcon } from './Icons/Planting.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/PDFIcon.svg';
import { ReactComponent as ConnectedIcon } from '../../../assets/images/CircledCheckGreen.svg';
import { ReactComponent as Envelope } from '../../../assets/images/Envelope.svg';
import { IGrowerResponse } from '../../../logic/Models/Responses/GrowerResponse';
import {
	Acres,
	getExpandedSpacing,
	getHeaderSpacing,
	IDashboardTableProps,
	keysToHeaders,
	StyledDashboardScrollableTableRows,
	StyledDashboardTableRows
} from './DashboardRows';
import { NameSegment } from './Segments/NameSegmentComponent';
import { ListChildComponentProps } from 'react-window';
import { ExpandedPlan } from './Segments/ExpandedPlan';
import { BoolDictionary } from '../../../logic/store/UI/UISlice';
import { Link } from 'react-router-dom';
import { Popover, Tooltip } from 'antd';
import { IconButton } from '../../../components/IconButton/IconButton';
import styled, { useTheme } from 'styled-components';
import { ClipLoader } from 'react-spinners';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { IOptionItem } from '../../../components/Dropdown/IOptionItem';
import { IGrowerUpdateRequest } from '../../../logic/store/Grower/GrowerThunks';
import moment from 'moment';
import { Button } from '../../../components/Button/Button';

const StyledIconButton = styled(IconButton)`
	border-radius: 25px;
	height: 80%;
	@media (hover: none) {
		width: 35px;
	}
	@media (hover: hover) {
		width: 40px;
	}
`;

// Execute-specific components
const MaxScript = (props) =>
{
	const { data } = props;
	return <StyledRowCell>
		<CirclePlusIcon style={{ visibility: !data ? 'visible' : 'hidden' }} />
		<PlantingIcon style={{ visibility: data ? 'visible' : 'hidden' }} />
	</StyledRowCell>;
};


interface IJdConnectProps extends IGrowerResponse
{
	CreateJdConnectInvitation: (growerId: string) => void;
	deleteJdConnection: (growerId: string) => void;
}

const StyledConnectedIcon = styled(ConnectedIcon)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

const StyledEnvelopeIcon = styled(Envelope)<{ fillcolor: string }>`
	path {
		fill: ${props => props.fillcolor};
	}
`;

const JdConnect = (props: IJdConnectProps) =>
{
	const { Id, JdConnectStatus, deleteJdConnection, CreateJdConnectInvitation,  } = props;
	const theme = useTheme();

	const convertAndDisplayDate = (dateValue: Date, title: string) =>
	{
		const lastBoundaryImport = moment.parseZone(dateValue).local();
		const date = lastBoundaryImport.format('MM/DD/YYYY');
		const time = lastBoundaryImport.format('hh:mm A');

		return (
			<div style={{ marginBottom: 5 }}>
				<span style={{ marginRight: 5, fontWeight: theme.fontWeights.bold }}>{title}:</span>
				<span>{date}  {time}</span>
			</div>
		);
	};

	const popOverContent = (
		<div>
			{
				JdConnectStatus?.LastJdConnectInvite && convertAndDisplayDate(JdConnectStatus?.LastJdConnectInvite, 'Last Invitation Sent')
			}
			{
				JdConnectStatus?.LastBoundaryImport && convertAndDisplayDate(JdConnectStatus?.LastBoundaryImport, 'Last Boundary Import')
			}
			{
				JdConnectStatus?.LastDataImport && convertAndDisplayDate(JdConnectStatus?.LastDataImport, 'Last Data Import')
			}
			{
				JdConnectStatus?.LastDataScan && convertAndDisplayDate(JdConnectStatus?.LastDataScan, 'Last Date Scan')
			}
			{
				JdConnectStatus?.IsConnected &&
				<div style={{ display: 'flex', flexDirection: 'column'}}>
					<Button variant='main' onClick={() => deleteJdConnection(Id)}>Remove Grower's John Deere Connection</Button>
				</div>
			}
			{
				!JdConnectStatus?.IsConnected && JdConnectStatus?.LastJdConnectInvite &&
				<div style={{ display: 'flex', flexDirection: 'column'}}>
					<Button variant='main' onClick={() => CreateJdConnectInvitation(Id)}>Send New Invitation</Button>
				</div>
			}
		</div>
	);
	
	return (
		<StyledRowCell>
			{
				JdConnectStatus.ConnectionStatus === 'Connected' || JdConnectStatus.ConnectionStatus === 'Invited' ?
					<Popover
						content={popOverContent}
						title={`John Deere Status: ${JdConnectStatus.ConnectionStatus}`}
						trigger='click'
					>
						{
							JdConnectStatus.ConnectionStatus === 'Connected' ?
								<StyledConnectedIcon
									width={20}
									height={20}
									fillcolor={theme.colors.primary}
									style={{ cursor: 'pointer' }}
								/>
								:
								<StyledEnvelopeIcon
									width={20}
									height={20}
									fillcolor={theme.colors.primary}
									style={{ cursor: 'pointer' }}
								/>
						}
						
					</Popover>
					:
					<CirclePlusIcon style={{ cursor: 'pointer' }} onClick={() => CreateJdConnectInvitation(Id)} />
			}
		</StyledRowCell>
	);
};

interface IMachineDataProps extends IGrowerResponse
{
	SetSelectedGrowerId: (growerId: string) => void;
}
const MachineData = React.memo<IGrowerResponse>(
	function MachineData(props: IMachineDataProps)
	{
		const { Id, SetSelectedGrowerId } = props;
		return (
			<Tooltip title={'Open FileUpload'}>
				<StyledRowCell style={{ margin: 0, height: '100%' }}>
					<Link className='FileUploadLink' to='/dashboard/fileupload' onClick={() =>{SetSelectedGrowerId(Id);}} style={{ display: 'contents', }}>
						<CirclePlusIcon />
					</Link>
				</StyledRowCell>
			</Tooltip>
		);
	}
);

interface IDownloadGrowerCropHealthPdfProps extends IGrowerResponse
{
	downloadGrowerCropHealth: (selectedGrowerId: string) => void;
	isDownloadEnabled: boolean;
	cropYear: number;
}

const DownloadGrowerCropHealthPdf = React.memo<IGrowerResponse>(function DownloadGrowerCropHealthPdf(props: IDownloadGrowerCropHealthPdfProps)
{
	const { Id, SeasonData, cropYear, isDownloadEnabled, downloadGrowerCropHealth } = props;

	const currentSeason = SeasonData.find((season) => season.SeasonYear === cropYear);
	const hasCropHealthDataAvailable: boolean = currentSeason?.HasCropHealthDataAvailable;

	return (
		<Tooltip title={isDownloadEnabled ? hasCropHealthDataAvailable ?
			'Download Crop Health PDF' : 'To view Crop Health, add a Planting Date and Actual Seed Assignments to a Field.'
			: 'Crop Health Pdf Coming Soon!'}>
			<StyledRowCell style={{ margin: 0, height: '100%' }}>
				<StyledIconButton
					onClick={() => downloadGrowerCropHealth(Id)}
					disabled={!isDownloadEnabled || !hasCropHealthDataAvailable}
				>
					<DownloadIcon />
				</StyledIconButton>
			</StyledRowCell>
		</Tooltip>
	);
});

interface IDownloadGrowerEndOfYearSummaryPdfProps extends IGrowerResponse
{
	downloadGrowerEndOfYearSummary: (selectedGrowerId: string) => void;
	isDownloadEnabled: boolean;
	isDownloading: boolean;
	cropYear: number;
}

const DownloadGrowerEndOfYearSummaryPdf = React.memo<IGrowerResponse>(function DownloadGrowerEndOfYearSummaryPdf(props: IDownloadGrowerEndOfYearSummaryPdfProps)
{
	const { Id, SeasonData, cropYear, isDownloadEnabled, isDownloading, downloadGrowerEndOfYearSummary } = props;
	const theme = useTheme();

	const currentSeason = SeasonData.find((season) => season.SeasonYear === cropYear);
	const hasEndOfYearSummaryDataAvailable: boolean = currentSeason?.HasEndOfYearSummaryDataAvailable;

	return (
		<Tooltip title={isDownloading ? 'Generating the End of Year Summary PDF...' : isDownloadEnabled ? hasEndOfYearSummaryDataAvailable ?
			'Download End of Year Summary PDF' : 'No End of Year Summary data available for this grower yet.'
			: 'End of Year Summary Pdf Coming Soon!'}>
			<StyledRowCell style={{ margin: 0, height: '100%' }}>
				<StyledIconButton
					onClick={() => downloadGrowerEndOfYearSummary(Id)}
					disabled={!isDownloadEnabled || !hasEndOfYearSummaryDataAvailable || isDownloading}
				>
					{isDownloading ? <ClipLoader size={16} color={theme.colors.primary} /> : <DownloadIcon />}
				</StyledIconButton>
			</StyledRowCell>
		</Tooltip>
	);
});

interface ISelectGrowerPDFLanguageProps extends IGrowerResponse
{
	updateGrower: (req: IGrowerUpdateRequest) => void;
}

const SelectGrowerPDFLanguage = React.memo<IGrowerResponse>(function SelectGrowerPDFLanguage(props: ISelectGrowerPDFLanguageProps)
{
	const { Id, LanguagePreference, updateGrower } = props;

	const languageOptions: IOptionItem[] =
	[
		{
			label: 'English',
			value: 'en'
		},
		{
			label: 'French-Canadian',
			value: 'fr-CA'
		}
	];

	const selectLanguage = useCallback((value: string) =>
	{
		updateGrower({ GrowerId: Id, RequestBody: { LanguagePreference: value }});
	},[Id, updateGrower]);

	return (
		<div style={{ display: 'flex', justifyContent: 'center' }}>
			<Dropdown
				variant='outlinednarrow'
				options={languageOptions}
				onSelect={selectLanguage}
				selected={LanguagePreference}
				placeholder='Select a Language'
				className='Language_Dropdown'
				menuStyle={{ maxHeight: 250, overflow: 'auto' }}
			/>
		</div>
	);
});

interface IExecuteDashboardTableProps extends IDashboardTableProps
{
	isDownloadEnabled: boolean;
	isEndOfYearSummaryDownloadEnabled: boolean;
	isJdConnectEnabled: boolean;
	downloadGrowerCropHealth: (selectedGrowerId: string) => void;
	downloadGrowerEndOfYearSummary: (selectedGrowerId: string) => void;
	downloadingEndOfYearSummaryGrowerIds: Set<string>;
	UpdateGrower: (req: IGrowerUpdateRequest) => void;
	deleteGrowerJdConnection: (growerId: string) => void;
}

export const ExecuteDashboardTable = (props: IExecuteDashboardTableProps) =>
{
	const {
		cropYear,
		currentCropYear,
		DashboardSort,
		DashboardTableExpanded,
		growers,
		isDownloadEnabled,
		isEndOfYearSummaryDownloadEnabled,
		userIsGrower,
		isJdConnectEnabled,
		onSort,
		downloadGrowerCropHealth,
		downloadGrowerEndOfYearSummary,
		downloadingEndOfYearSummaryGrowerIds,
		SetExpanded,
		SetSelectedGrowerId,
		UpdateGrower,
		CreateJdConnectInvitation,
		deleteGrowerJdConnection,
	} = props;

	// Matches EndOfYearSummaryEligibility.AvailableFromMonth on the backend - the current
	// (calendar, not crop) year's summary isn't offered until harvest is mostly done; past years
	// are always in season.
	const now = new Date();
	const isEndOfYearSummaryInSeason = cropYear < now.getFullYear()
		|| (cropYear === now.getFullYear() && now.getMonth() + 1 >= 10);
	const showEndOfYearSummaryColumn = isEndOfYearSummaryDownloadEnabled && isEndOfYearSummaryInSeason;

	const columns = useMemo(() =>
	{
		const base = isJdConnectEnabled ? [
			'Name / Segment',
			'Accepted Acres / Mapped Acres',
			'MaxScript',
			'Machine Data',
			'John Deere Ops',
			'Crop Health',
			'End of Year Summary',
			'PDF Language'
		] : [
			'Name / Segment',
			'Accepted Acres / Mapped Acres',
			'MaxScript',
			'Machine Data',
			'Crop Health',
			'End of Year Summary',
			'PDF Language'
		];

		return showEndOfYearSummaryColumn ? base : base.filter((column) => column !== 'End of Year Summary');
	}, [isJdConnectEnabled, showEndOfYearSummaryColumn]);
	const headerSpacing = getHeaderSpacing(columns);
	const expandedSpacing = getExpandedSpacing(columns);

	const RenderTableRow = useCallback(({ data, index, style }: ListChildComponentProps<{ rows: IGrowerResponse; ref: React.RefObject<any> }>) =>
	{
		const { rows, ref } = data;
		const grower = rows[index];

		const onExpand = (idEntry: BoolDictionary) =>
		{
			if (ref && ref.current?.resetAfterIndex)
			{
				ref.current?.resetAfterIndex(index, false);
			}
			SetExpanded(idEntry);
		};

		const items = () =>
		{
			const eoysCell = showEndOfYearSummaryColumn && (
				<DownloadGrowerEndOfYearSummaryPdf key={`execute-cell-eoys-${grower.Name}-${index}`} {...grower}
					downloadGrowerEndOfYearSummary={downloadGrowerEndOfYearSummary}
					isDownloadEnabled={isEndOfYearSummaryDownloadEnabled}
					isDownloading={downloadingEndOfYearSummaryGrowerIds.has(grower.Id)}
					cropYear={cropYear}
				/>
			);

			if (isJdConnectEnabled)
			{
				return ([
					<NameSegment key={`execute-cell-name-${grower.Name}-${index}`} {...grower} DashboardTableExpanded={DashboardTableExpanded} />,
					<Acres key={`execute-cell-acres-${grower.Name}-${index}`} {...grower} cropYear={cropYear} />,
					<MaxScript key={`execute-cell-maxscript-${grower.Name}-${index}`} {...grower} />,
					<MachineData key={`execute-cell-machinedata-${grower.Name}-${index}`} {...grower} SetSelectedGrowerId={SetSelectedGrowerId} />,
					<JdConnect
						key={`execute-cell-jdconnect-${grower.Name}-${index}`}
						{...grower}
						CreateJdConnectInvitation={CreateJdConnectInvitation}
						deleteJdConnection={deleteGrowerJdConnection}
					/>,
					<DownloadGrowerCropHealthPdf key={`execute-cell-crophealth-${grower.Name}-${index}`} {...grower}
						downloadGrowerCropHealth={downloadGrowerCropHealth}
						isDownloadEnabled={isDownloadEnabled}
						cropYear={cropYear}
					/>,
					eoysCell,
					<SelectGrowerPDFLanguage key={`execute-cell-pdflanguage-${grower.Name}=`} {...grower} updateGrower={UpdateGrower} />,
				].filter(Boolean));
			}
			else
			{
				return ([
					<NameSegment key={`execute-cell-name-${grower.Name}-${index}`} {...grower} DashboardTableExpanded={DashboardTableExpanded} />,
					<Acres key={`execute-cell-acres-${grower.Name}-${index}`} {...grower} cropYear={cropYear} />,
					<MaxScript key={`execute-cell-maxscript-${grower.Name}-${index}`} {...grower} />,
					<MachineData key={`execute-cell-machinedata-${grower.Name}-${index}`} {...grower} SetSelectedGrowerId={SetSelectedGrowerId} />,
					<DownloadGrowerCropHealthPdf key={`execute-cell-crophealth-${grower.Name}-${index}`} {...grower}
						downloadGrowerCropHealth={downloadGrowerCropHealth}
						isDownloadEnabled={isDownloadEnabled}
						cropYear={cropYear}
					/>,
					eoysCell,
					<SelectGrowerPDFLanguage key={`execute-cell-pdflanguage-${grower.Name}=`} {...grower} updateGrower={UpdateGrower} />,
				].filter(Boolean));
			}
		};

		return (
			<div key={`tableRow-${index}`}
				style={{ marginTop: '10px', ...style }}>
				<TableRow
					Id={grower.Id}
					items={items()}
					expanded={DashboardTableExpanded[grower.Id]}
					columnSpacing={headerSpacing}
					setExpanded={onExpand}
					expandedSection={
						<ExpandedPlan
							Grower={grower}
							currentTab={'Execute'}
							expandedSpacing={expandedSpacing}
							SetSelectedGrowerId={SetSelectedGrowerId}
							cropYear={cropYear}
							currentCropYear={currentCropYear}
							userIsGrower={userIsGrower}
						/>
					}
				/>
			</div>
		);
	}, [DashboardTableExpanded, cropYear, currentCropYear, isDownloadEnabled, isEndOfYearSummaryDownloadEnabled, SetSelectedGrowerId, downloadGrowerCropHealth, downloadGrowerEndOfYearSummary, downloadingEndOfYearSummaryGrowerIds]);

	return (
		<StyledContainer>
			<TableHeader headers={columns}
				columnSpacing={headerSpacing}
				initialSorts={{ [keysToHeaders[DashboardSort.field]]: DashboardSort.sortDirection }}
				sortActions={{ 'Name / Segment': (order) => onSort(order, 'Name / Segment') }}
				defaultSort={{ headerName: 'Name / Segment', direction: 'ascending' }} />
			<StyledDashboardScrollableTableRows>
				<StyledDashboardTableRows rows={growers}
					renderComponent={RenderTableRow}
					itemSize={(index) => DashboardTableExpanded[growers[index].Id] ? 265 : 85} />
			</StyledDashboardScrollableTableRows>
		</StyledContainer>
	);
};
